import { useEffect, useMemo, useRef, useState } from 'react';
import { ClipboardList } from 'lucide-react';
import { CommentActionModal } from './CommentComposer';
import {
  fetchSlDispositionOptions,
  postSlDisposition,
} from '../../services/referralService';

// Greyed placeholder shown in the Reason/Action selects when the chosen
// response is locked (Reason/Action forbidden). The label text isn't business
// vocab — it's a generic "not applicable" affordance.
const LOCKED_PLACEHOLDER = 'Not applicable';

// All cascade behaviour is read from the options payload — nothing about the SL
// vocabulary (responses/reasons/actions/required/locked) is hardcoded here.
//
// Every submission APPENDS a new SL activity — it never supersedes the prior
// one. SL comments/dispositions are an append-only audit trail (the same way
// carrier comments are), and the "current" SL response is derived as the latest
// disposition. An earlier version posted `replaces` (soft-deleting the previous
// SL activity) on every save, which collapsed same-day SL comments to only the
// last one (PRODUCT-334). Edit-in-place via `replaces` remains a backend
// capability, but it is not wired to this add flow.
// Resolve a stored display label back to its option `value` (code) within the
// given response's option list. The CE row / drawer carry sl_reason and
// sl_action as the display labels (that's what buildPatch persists), but the
// selects key off the code, so we map label -> value to pre-populate them.
const codeForLabel = (optionsForResponse, label) => {
  if (!label) return '';
  return optionsForResponse.find((o) => o.label === label)?.value ?? '';
};

const SlDispositionModal = ({
  open,
  onClose,
  onSuccess,
  refId,
  currentResponse = null,
  // Display labels for the current SL reason/action (as shown in the CE table /
  // drawer). Pre-populated into the Reason/Action selects on open.
  currentReason = null,
  currentAction = null,
  initialFocusField = null,
}) => {
  const [options, setOptions] = useState(null);
  const [optionsState, setOptionsState] = useState('idle'); // 'idle' | 'loading' | 'loaded' | 'error'

  const [response, setResponse] = useState('');
  const [reasonCode, setReasonCode] = useState('');
  const [actionCode, setActionCode] = useState('');
  const [comment, setComment] = useState('');
  const [showExternal, setShowExternal] = useState(false);
  const [needsReply, setNeedsReply] = useState(false);

  const [status, setStatus] = useState('idle'); // 'idle' | 'submitting' | 'success' | 'error'
  const [errorMessage, setErrorMessage] = useState('');

  const textareaRef = useRef(null);
  const responseRef = useRef(null);
  const successTimerRef = useRef(null);

  // Reset all form state every time the modal opens.
  useEffect(() => {
    if (!open) return;
    setResponse('');
    setReasonCode('');
    setActionCode('');
    setComment('');
    setShowExternal(false);
    setNeedsReply(false);
    setStatus('idle');
    setErrorMessage('');
    if (successTimerRef.current) {
      clearTimeout(successTimerRef.current);
      successTimerRef.current = null;
    }
  }, [open]);

  useEffect(() => {
    return () => {
      if (successTimerRef.current) clearTimeout(successTimerRef.current);
    };
  }, []);

  // Load the cascade options on open.
  useEffect(() => {
    if (!open) return undefined;
    let cancelled = false;
    setOptions(null);
    setOptionsState('loading');
    fetchSlDispositionOptions()
      .then((data) => {
        if (cancelled) return;
        setOptions(data);
        setOptionsState('loaded');
        // Seed the response from the current value, else the payload default.
        const seededResponse = currentResponse ?? data.default_response ?? '';
        setResponse(seededResponse);
        // Seed reason/action from the current labels, scoped to the seeded
        // response's option lists. Locked responses have no reason/action to
        // restore, so this naturally resolves to '' for them.
        setReasonCode(
          codeForLabel((data.reasons_by_response ?? {})[seededResponse] ?? [], currentReason),
        );
        setActionCode(
          codeForLabel((data.actions_by_response ?? {})[seededResponse] ?? [], currentAction),
        );
      })
      .catch(() => {
        if (cancelled) return;
        setOptionsState('error');
      });
    return () => {
      cancelled = true;
    };
  }, [open, currentResponse, currentReason, currentAction]);

  // Best-effort focus. initialFocusField is a hint; if it's not the response
  // field we just focus the textarea like CommentModal.
  useEffect(() => {
    if (!open || optionsState !== 'loaded') return;
    if (initialFocusField === 'sl_response' && responseRef.current) {
      responseRef.current.focus();
    } else if (textareaRef.current) {
      textareaRef.current.focus();
    }
  }, [open, optionsState, initialFocusField]);

  const isLocked = useMemo(
    () => !!options && (options.locked_responses ?? []).includes(response),
    [options, response],
  );
  const commentRequired = useMemo(
    () => !!options && !!(options.comment_required_by_response ?? {})[response],
    [options, response],
  );
  const reasonOptions = useMemo(
    () => (options ? (options.reasons_by_response ?? {})[response] ?? [] : []),
    [options, response],
  );
  const actionOptions = useMemo(
    () => (options ? (options.actions_by_response ?? {})[response] ?? [] : []),
    [options, response],
  );

  const handleResponseChange = (e) => {
    setResponse(e.target.value);
    // A new response invalidates the prior reason/action — both are scoped to
    // the response.
    setReasonCode('');
    setActionCode('');
    if (status === 'error') {
      setStatus('idle');
      setErrorMessage('');
    }
  };

  const handleCommentChange = (e) => {
    setComment(e.target.value);
    if (status === 'error') {
      setStatus('idle');
      setErrorMessage('');
    }
  };

  const optionsLoaded = optionsState === 'loaded';
  const trimmedComment = comment.trim();

  const submitDisabled =
    !optionsLoaded ||
    status === 'submitting' ||
    status === 'success' ||
    (!isLocked && (!reasonCode || !actionCode)) ||
    (commentRequired && trimmedComment.length < 1);

  const buildPatch = (created) => {
    // The patch carries the CE row's SL fields + last comment, sourced from the
    // submitted display values (the modal knows the chosen labels). This works
    // for both the table-cell path and the drawer path without an extra
    // round-trip, and sidesteps that the activities GET exposes action_code as
    // a raw code rather than its display label.
    const reasonLabel = reasonOptions.find((o) => o.value === reasonCode)?.label ?? null;
    const actionLabel = actionOptions.find((o) => o.value === actionCode)?.label ?? null;
    const patch = {
      sl_response: response,
      sl_reason: isLocked ? null : reasonLabel,
      sl_action: isLocked ? null : actionLabel,
    };
    // Only touch the Last Comment columns when a comment was actually posted —
    // locked responses can save without one, and we don't want to blank an
    // existing comment. The date is server-sourced from the created activity's
    // created_at so it matches what was persisted (no client clock skew).
    if (comment) {
      patch.latest_comment = comment;
      patch.last_comment_date = created?.created_at ?? null;
    }
    return patch;
  };

  const handleSubmit = async () => {
    const effectiveReasonCode = isLocked
      ? (options.response_reason_code_by_response ?? {})[response]
      : reasonCode;
    const payload = {
      reason_code: effectiveReasonCode,
      action_code: isLocked ? null : actionCode,
      comment,
      show_external: showExternal,
      needs_reply: needsReply,
    };
    setStatus('submitting');
    setErrorMessage('');
    try {
      const created = await postSlDisposition(refId, payload);
      if (onSuccess) onSuccess(buildPatch(created));
      setStatus('success');
      successTimerRef.current = setTimeout(() => {
        if (onClose) onClose();
      }, 1500);
    } catch (err) {
      setStatus('error');
      setErrorMessage(err?.message || 'Failed to save. Try again.');
    }
  };

  return (
    <CommentActionModal
      open={open}
      onClose={onClose}
      title="Add Comment & Update SL Response"
      contentTestId="sl-disposition-modal"
      idPrefix="sl-"
      comment={comment}
      onCommentChange={handleCommentChange}
      textareaRef={textareaRef}
      showExternal={showExternal}
      onShowExternalChange={setShowExternal}
      needsReply={needsReply}
      onNeedsReplyChange={setNeedsReply}
      needsReplyLabel="Request response from TPA/Carrier"
      commentMinHeightClass="min-h-[120px]"
      status={status}
      errorMessage={errorMessage}
      onSubmit={handleSubmit}
      submitDisabled={submitDisabled}
      onCancel={onClose}
      successMessage="SL response updated."
      submitTestId="sl-submit"
    >
      <section>
        <div className="flex gap-2 items-center">
          <ClipboardList className="h-4 w-4 text-primary" aria-hidden="true" />
          <span className="text-sm font-bold tracking-tight text-foreground">SL Response</span>
        </div>
        <div
          data-testid="sl-response-card"
          className="mt-2 border border-border bg-muted/30 p-3 rounded-lg"
        >
          <div className="grid grid-cols-[max-content_minmax(0,1fr)] gap-x-3 gap-y-2 items-center w-1/2">
            <label
              htmlFor="sl-response-select"
              className="text-xs font-semibold whitespace-nowrap text-foreground text-right"
            >
              SL Response
            </label>
            <select
              id="sl-response-select"
              data-testid="sl-response-select"
              ref={responseRef}
              value={response}
              onChange={handleResponseChange}
              disabled={!optionsLoaded}
              className="h-8 w-full text-xs rounded-md border border-input bg-background px-3 text-foreground focus:outline-none focus:border-ring disabled:opacity-60 disabled:pointer-events-none"
            >
              {!optionsLoaded && <option value="">Loading…</option>}
              {optionsLoaded &&
                (options.responses ?? []).map((name) => (
                  <option key={name} value={name}>
                    {name}
                  </option>
                ))}
            </select>

            <label
              htmlFor="sl-reason-select"
              className="text-xs font-semibold whitespace-nowrap text-foreground text-right"
            >
              SL Reason
            </label>
            <select
              id="sl-reason-select"
              data-testid="sl-reason-select"
              value={isLocked ? '' : reasonCode}
              onChange={(e) => setReasonCode(e.target.value)}
              disabled={!optionsLoaded || isLocked}
              className="h-8 w-full text-xs rounded-md border border-input bg-background px-3 text-foreground focus:outline-none focus:border-ring disabled:opacity-60 disabled:pointer-events-none"
            >
              {isLocked ? (
                <option value="">{LOCKED_PLACEHOLDER}</option>
              ) : (
                <>
                  <option value="">Select a reason…</option>
                  {reasonOptions.map((opt) => (
                    <option key={opt.value} value={opt.value}>
                      {opt.label}
                    </option>
                  ))}
                </>
              )}
            </select>

            <label
              htmlFor="sl-action-select"
              className="text-xs font-semibold whitespace-nowrap text-foreground text-right"
            >
              SL Action
            </label>
            <select
              id="sl-action-select"
              data-testid="sl-action-select"
              value={isLocked ? '' : actionCode}
              onChange={(e) => setActionCode(e.target.value)}
              disabled={!optionsLoaded || isLocked}
              className="h-8 w-full text-xs rounded-md border border-input bg-background px-3 text-foreground focus:outline-none focus:border-ring disabled:opacity-60 disabled:pointer-events-none"
            >
              {isLocked ? (
                <option value="">{LOCKED_PLACEHOLDER}</option>
              ) : (
                <>
                  <option value="">Select an action…</option>
                  {actionOptions.map((opt) => (
                    <option key={opt.value} value={opt.value}>
                      {opt.label}
                    </option>
                  ))}
                </>
              )}
            </select>
          </div>

          {optionsState === 'error' && (
            <p
              data-testid="sl-options-load-error"
              className="mt-3 text-xs text-destructive"
            >
              Failed to load SL response options.
            </p>
          )}
        </div>
      </section>
    </CommentActionModal>
  );
};

export { SlDispositionModal };
